import type { ProjectResponse } from '@automatick/shared-ng';
import { takeUntil } from 'rxjs/operators';
import {
  ScopeOwnedAsyncRequestTracker,
  type ScopeOwnedAsyncRequest,
} from './scope-owned-async';
import { WebChatSessionCatalogRuntime } from './web-chat-session-catalog.runtime';
import { WebChatStateStore } from './web-chat-state.store';
import {
  isCanonicalWebChatIdentifier,
  retainUniqueWebChatResources,
} from './web-chat.models';

interface ProjectRequestOwner {
  readonly request: ScopeOwnedAsyncRequest;
  readonly workspaceId: string;
}

export class WebChatProjectRuntime {
  private readonly requests = new ScopeOwnedAsyncRequestTracker();
  private destroyed = false;

  constructor(
    private readonly state: WebChatStateStore,
    private readonly sessions: WebChatSessionCatalogRuntime,
  ) {}

  destroy(): void {
    this.destroyed = true;
    this.requests.invalidate();
  }

  loadProjectsForWorkspace(workspaceId: string | null | undefined): void {
    const normalizedWorkspaceId = canonicalIdentifier(workspaceId);
    if (!normalizedWorkspaceId) {
      this.requests.invalidate();
      this.patch({ projects: [], selectedProjectId: null });
      return;
    }
    if (this.activeWorkspaceId !== normalizedWorkspaceId) {
      return;
    }

    const owner: ProjectRequestOwner = {
      request: this.requests.begin(normalizedWorkspaceId),
      workspaceId: normalizedWorkspaceId,
    };

    this.state.projectsClient
      .listProjects({ includeArchived: false })
      .pipe(takeUntil(this.state.destroy$))
      .subscribe({
        next: (projects) => this.applyProjectList(owner, projects),
        error: () => this.applyProjectList(owner, []),
      });
  }

  createProject(name: string): void {
    const normalizedName = name.trim();
    const workspaceId = this.activeWorkspaceId;
    if (!normalizedName || !workspaceId) {
      return;
    }

    const owner: ProjectRequestOwner = {
      request: this.requests.begin(workspaceId),
      workspaceId,
    };

    this.state.projectsClient
      .createProject({ body: { name: normalizedName } })
      .pipe(takeUntil(this.state.destroy$))
      .subscribe({
        next: (project) => {
          if (!this.owns(owner) || !this.isOwnedProject(owner, project)) {
            return;
          }

          const projectId = project.projectId;
          const projects = [
            project,
            ...this.state.stateSubject.value.projects.filter(
              (item) => item.projectId !== projectId,
            ),
          ].sort(compareProjects);

          this.patch({
            projects,
            selectedProjectId: projectId,
            errorMessage: '',
          });

          this.sessions.setProjectScope(projectId);
        },
        error: () => {
          if (this.owns(owner)) {
            this.patch({
              errorMessage: 'Failed to create project. Please try again.',
            });
          }
        },
      });
  }

  private applyProjectList(
    owner: ProjectRequestOwner,
    projects: readonly ProjectResponse[],
  ): void {
    if (!this.owns(owner)) {
      return;
    }

    const ownedProjects = retainUniqueWebChatResources(
      projects.filter((project) => this.isOwnedProject(owner, project)),
      (project) => project.projectId,
    );
    const currentProjectId =
      this.state.stateSubject.value.session?.projectId ??
      this.state.stateSubject.value.selectedProjectId;
    const selectedProjectId =
      currentProjectId &&
      ownedProjects.some((project) => project.projectId === currentProjectId)
        ? currentProjectId
        : null;

    this.patch({ projects: ownedProjects, selectedProjectId });
  }

  private owns(owner: ProjectRequestOwner): boolean {
    return (
      !this.destroyed &&
      this.activeWorkspaceId === owner.workspaceId &&
      this.requests.owns(owner.request, owner.workspaceId)
    );
  }

  private isOwnedProject(
    owner: ProjectRequestOwner,
    project: ProjectResponse,
  ): project is ProjectResponse & { projectId: string; workspaceId: string } {
    return (
      isCanonicalWebChatIdentifier(project.projectId) &&
      project.workspaceId === owner.workspaceId
    );
  }

  private get activeWorkspaceId(): string | null {
    return canonicalIdentifier(
      this.state.appState.getStateSnapshot().activeWorkspaceId,
    );
  }

  private patch(partial: Parameters<WebChatStateStore['patch']>[0]): void {
    this.state.patch(partial);
  }
}

function compareProjects(
  left: ProjectResponse,
  right: ProjectResponse,
): number {
  return (left.name ?? left.slug ?? '').localeCompare(
    right.name ?? right.slug ?? '',
  );
}

function canonicalIdentifier(value: string | null | undefined): string | null {
  const normalized = value?.trim();
  return normalized ? normalized : null;
}
